import * as THREE from 'three'
import { WORLD } from './constants.js'

// Ambient life for the backdrop: gusting wind that sways trees and grass
// and pushes clouds, passing bird flocks, blowing leaves, dusk fireflies and
// camera shake on heavy impacts. Purely visual; physics never reads it.

const BIRD_Z = -3.2
const LEAF_Z = -1.4
const FIREFLY_Z = -1.2
const MAX_LEAVES = 18
const MAX_FIREFLIES = 16
const FLOCK_INTERVAL = [9, 20]
const GUST_INTERVAL = [2.5, 6.5]
const SHAKE_DECAY = 7

export class Environment {
    constructor(parent) {
        this.group = new THREE.Group()
        parent.add(this.group)
        this.time = 0
        this.wind = 0.3
        this.windTarget = 0.3
        this.nextGustIn = 2
        this.cloudTime = 0
        this.nextFlockIn = 3
        this.shakeAmount = 0
        this.shakeOffset = { x: 0, y: 0 }
        this.swayUniforms = {
            uTime: { value: 0 },
            uWind: { value: 0.3 }
        }
        this.birds = []
        this.leaves = []
        this.fireflies = []
        this.birdTextures = [makeBirdTexture(true), makeBirdTexture(false)]
        this.leafGeometry = new THREE.PlaneGeometry(0.16, 0.1)
        this.fireflyTexture = makeGlowTexture()
    }

    // Makes the upper part of a backdrop layer sway with the wind.
    // strength is the sideways reach (world units) at the top of the plane.
    addSway(material, strength) {
        material.onBeforeCompile = (shader) => {
            shader.uniforms.uTime = this.swayUniforms.uTime
            shader.uniforms.uWind = this.swayUniforms.uWind
            shader.uniforms.uSwayStrength = { value: strength }
            shader.vertexShader = shader.vertexShader
                .replace('#include <common>', `#include <common>
uniform float uTime;
uniform float uWind;
uniform float uSwayStrength;`)
                .replace('#include <begin_vertex>', `#include <begin_vertex>
float swayWeight = clamp(uv.y, 0.0, 1.0);
swayWeight *= swayWeight;
float sway = sin(uTime * 1.6 + position.x * 0.42) * (0.35 + uWind * 0.65)
    + sin(uTime * 3.1 + position.x * 1.3) * 0.18 * uWind
    + uWind * 0.6;
transformed.x += sway * uSwayStrength * swayWeight;`)
        }
        material.needsUpdate = true
    }

    shake(intensity) {
        this.shakeAmount = Math.min(0.35, Math.max(this.shakeAmount, intensity))
    }

    // view: { x, y, width, height } of the visible world; dusk: show fireflies.
    update(delta, view, { dusk = false } = {}) {
        const dt = Math.min(0.05, Math.max(0, delta))
        this.time += dt
        this.updateWind(dt)
        this.cloudTime += dt * (0.55 + this.wind * 1.1)
        this.swayUniforms.uTime.value = this.time
        this.swayUniforms.uWind.value = this.wind
        this.updateShake(dt)
        this.updateBirds(dt, view)
        this.updateLeaves(dt, view)
        this.updateFireflies(dt, view, dusk)
    }

    updateWind(dt) {
        this.nextGustIn -= dt
        if (this.nextGustIn <= 0) {
            this.nextGustIn = randomRange(...GUST_INTERVAL)
            // Mostly a breeze, sometimes a proper gust.
            this.windTarget = Math.random() < 0.3 ? randomRange(0.7, 1) : randomRange(0.1, 0.45)
        }

        this.wind += (this.windTarget - this.wind) * Math.min(1, dt * 0.8)
    }

    updateShake(dt) {
        if (this.shakeAmount <= 0.001) {
            this.shakeAmount = 0
            this.shakeOffset.x = 0
            this.shakeOffset.y = 0
            return
        }

        this.shakeOffset.x = (Math.random() * 2 - 1) * this.shakeAmount
        this.shakeOffset.y = (Math.random() * 2 - 1) * this.shakeAmount * 0.6
        this.shakeAmount *= Math.exp(-SHAKE_DECAY * dt)
    }

    updateBirds(dt, view) {
        this.nextFlockIn -= dt
        if (this.nextFlockIn <= 0 && this.birds.length === 0) {
            this.nextFlockIn = randomRange(...FLOCK_INTERVAL)
            this.spawnFlock(view)
        }

        for (const bird of this.birds) {
            bird.flap += dt * bird.flapSpeed
            const wingsUp = Math.sin(bird.flap) > 0
            bird.mesh.material.map = this.birdTextures[wingsUp ? 0 : 1]
            bird.mesh.position.x += bird.vx * dt
            bird.mesh.position.y = bird.baseY + Math.sin(this.time * 1.3 + bird.phase) * 0.18
        }

        this.birds = this.birds.filter((bird) => {
            const gone = Math.abs(bird.mesh.position.x - view.x) > view.width / 2 + 4
                && Math.sign(bird.mesh.position.x - view.x) === Math.sign(bird.vx)
            if (gone) {
                this.group.remove(bird.mesh)
                bird.mesh.material.dispose()
            }
            return !gone
        })
    }

    spawnFlock(view) {
        const direction = Math.random() < 0.65 ? 1 : -1
        const count = 3 + Math.floor(Math.random() * 5)
        const startX = view.x - direction * (view.width / 2 + 2)
        const startY = view.y + view.height * randomRange(0.12, 0.34)
        const speed = randomRange(1.6, 2.6)

        for (let index = 0; index < count; index += 1) {
            // Loose V formation.
            const rank = Math.ceil(index / 2)
            const side = index % 2 === 0 ? 1 : -1
            const size = randomRange(0.32, 0.46)
            const mesh = new THREE.Mesh(
                new THREE.PlaneGeometry(size, size * 0.5),
                new THREE.MeshBasicMaterial({
                    map: this.birdTextures[0],
                    transparent: true,
                    opacity: 0.82,
                    depthWrite: false
                })
            )
            mesh.scale.x = direction
            mesh.renderOrder = -15
            mesh.position.set(startX - direction * rank * 0.55, startY + side * rank * 0.28, BIRD_Z)
            this.group.add(mesh)
            this.birds.push({
                mesh,
                vx: direction * speed,
                baseY: mesh.position.y,
                phase: Math.random() * Math.PI * 2,
                flap: Math.random() * Math.PI * 2,
                flapSpeed: randomRange(9, 13)
            })
        }
    }

    updateLeaves(dt, view) {
        const spawnRate = 0.4 + this.wind * 3.2
        if (this.leaves.length < MAX_LEAVES && Math.random() < spawnRate * dt) {
            this.spawnLeaf(view)
        }

        for (const leaf of this.leaves) {
            leaf.age += dt
            leaf.mesh.position.x += (leaf.drift + this.wind * 2.4) * dt
            leaf.mesh.position.y += (-0.45 + Math.sin(leaf.age * leaf.wobble) * 0.35) * dt
            leaf.mesh.rotation.z += leaf.spin * dt
            leaf.mesh.material.opacity = Math.min(0.9, leaf.age * 2) * Math.max(0, 1 - leaf.age / leaf.life)
        }

        this.leaves = this.leaves.filter((leaf) => {
            const done = leaf.age >= leaf.life || leaf.mesh.position.y < WORLD.groundY - 0.5
            if (done) {
                this.group.remove(leaf.mesh)
                leaf.mesh.material.dispose()
            }
            return !done
        })
    }

    spawnLeaf(view) {
        const colors = [0xd08a3a, 0xb8692f, 0x9aa43f, 0xe0b04a]
        const mesh = new THREE.Mesh(
            this.leafGeometry,
            new THREE.MeshBasicMaterial({
                color: colors[Math.floor(Math.random() * colors.length)],
                transparent: true,
                opacity: 0,
                depthWrite: false,
                side: THREE.DoubleSide
            })
        )
        mesh.position.set(
            view.x - view.width / 2 + Math.random() * view.width * 0.7,
            view.y + view.height * randomRange(0.0, 0.45),
            LEAF_Z
        )
        mesh.renderOrder = -2
        this.group.add(mesh)
        this.leaves.push({
            mesh,
            age: 0,
            life: randomRange(4, 8),
            drift: randomRange(0.1, 0.6),
            wobble: randomRange(2, 4),
            spin: randomRange(-3, 3)
        })
    }

    updateFireflies(dt, view, dusk) {
        if (dusk && this.fireflies.length < MAX_FIREFLIES && Math.random() < 2 * dt) {
            this.spawnFirefly(view)
        }

        for (const fly of this.fireflies) {
            fly.age += dt
            fly.mesh.position.x += Math.sin(fly.age * 0.9 + fly.phase) * 0.25 * dt
            fly.mesh.position.y += Math.cos(fly.age * 1.3 + fly.phase) * 0.18 * dt
            const blink = 0.5 + 0.5 * Math.sin(fly.age * fly.blink + fly.phase)
            const fade = Math.min(1, fly.age) * Math.max(0, Math.min(1, (fly.life - fly.age) / 1.5))
            fly.mesh.material.opacity = blink * fade * (dusk ? 0.9 : 0.4)
        }

        this.fireflies = this.fireflies.filter((fly) => {
            const done = fly.age >= fly.life
            if (done) {
                this.group.remove(fly.mesh)
                fly.mesh.material.dispose()
            }
            return !done
        })
    }

    spawnFirefly(view) {
        const mesh = new THREE.Mesh(
            new THREE.PlaneGeometry(0.22, 0.22),
            new THREE.MeshBasicMaterial({
                map: this.fireflyTexture,
                color: 0xfff1a0,
                transparent: true,
                opacity: 0,
                depthWrite: false,
                blending: THREE.AdditiveBlending
            })
        )
        mesh.position.set(
            view.x + (Math.random() - 0.5) * view.width,
            WORLD.groundY + randomRange(0.3, 2.6),
            FIREFLY_Z
        )
        mesh.renderOrder = -1
        this.group.add(mesh)
        this.fireflies.push({
            mesh,
            age: 0,
            life: randomRange(5, 10),
            phase: Math.random() * Math.PI * 2,
            blink: randomRange(1.5, 3.5)
        })
    }
}

function makeBirdTexture(wingsUp) {
    const canvas = document.createElement('canvas')
    canvas.width = 64
    canvas.height = 32
    const context = canvas.getContext('2d')
    context.strokeStyle = '#2b2622'
    context.lineWidth = 4
    context.lineCap = 'round'
    context.beginPath()
    if (wingsUp) {
        context.moveTo(4, 8)
        context.quadraticCurveTo(18, 4, 32, 20)
        context.quadraticCurveTo(46, 4, 60, 8)
    } else {
        context.moveTo(6, 22)
        context.quadraticCurveTo(18, 14, 32, 18)
        context.quadraticCurveTo(46, 14, 58, 22)
    }
    context.stroke()
    const texture = new THREE.CanvasTexture(canvas)
    texture.colorSpace = THREE.SRGBColorSpace
    return texture
}

function makeGlowTexture() {
    const canvas = document.createElement('canvas')
    canvas.width = 32
    canvas.height = 32
    const context = canvas.getContext('2d')
    const gradient = context.createRadialGradient(16, 16, 0, 16, 16, 16)
    gradient.addColorStop(0, 'rgba(255,255,220,1)')
    gradient.addColorStop(0.35, 'rgba(255,236,140,0.6)')
    gradient.addColorStop(1, 'rgba(255,220,120,0)')
    context.fillStyle = gradient
    context.fillRect(0, 0, 32, 32)
    return new THREE.CanvasTexture(canvas)
}

function randomRange(min, max) {
    return min + Math.random() * (max - min)
}
